import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef, useContext } from 'react';
import { RiFolderLine, RiFolderOpenLine, RiCheckLine, RiCloseLine } from 'react-icons/ri';

import MissionRow from './MissionRow';
import { MissionTableContext } from './MissionTableContext';
import styles from './ProjectRow.module.scss';
import { FieldText } from '../kit/Field';
import MoreMenu from '../MoreMenu';

import AccordionCaret from '@/components/ui/AccordionCaret';
import EmptyList from '@/components/ui/EmptyList';
import { IconButton } from '@/components/ui/kit/Button';
import { updateProject, deleteProject } from '@/helpers/Requester';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const ProjectRow = ({ project }) => {
  const { handleSelectProject, handleRenameProject, handleDeleteProject, selectedProjectId } =
    useContext(MissionTableContext);
  const [isExpanded, setExpanded] = useState(true);
  const [isEditing, setEditing] = useState(false);
  const nameRef = useRef();

  useUpdateEffect(() => {
    if (isEditing) {
      nameRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (isEditing) return;

    handleSelectProject(project);
  };

  const handleKeyDown = (e) => {
    if (e.code === 'Enter') {
      doSave();
    } else if (e.code === 'Escape') {
      setEditing(false);
    }
  };

  const doSave = () => {
    const name = nameRef.current.value.trim();

    if (name === '') {
      notifier.error('Please enter the project name.');
      return;
    }
    if (name === project.name) {
      setEditing(false);
      return;
    }

    updateProject(project.id, name).then(({ success }) => {
      if (success) {
        handleRenameProject(project.id, name);
        setEditing(false);
      }
    });
  };

  const doDelete = () => {
    if (project.missions.length > 0) {
      notifier.error('This project contains missions. Please delete the missions first.');
      return;
    }

    deleteProject(project.id).then(({ success }) => {
      if (success) {
        handleDeleteProject(project.id);
        setEditing(false);
      }
    });
  };

  const toggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!isExpanded);
  };

  return (
    <div className={cx('container')}>
      <div className={cx(['header', { target: project.id === selectedProjectId }])} onClick={handleClick}>
        {isExpanded && <RiFolderOpenLine size={16} color="white" />}
        {!isExpanded && <RiFolderLine size={16} color="white" />}
        {!isEditing && (
          <div className={cx('name')}>
            <div className={cx('text')}>{project.name}</div>
            <div className={cx('count')}>{project.missions.length}</div>
          </div>
        )}
        {isEditing && (
          <FieldText
            ref={nameRef}
            defaultValue={project.name}
            maxLength={24}
            onKeyDown={handleKeyDown}
            className={cx('edit')}
          />
        )}
        {!isEditing && (
          <div className={cx('control')}>
            {!project.isDefault && (
              <MoreMenu
                data={[
                  { name: 'Rename', callback: () => setEditing(true) },
                  { name: 'Delete', callback: doDelete },
                ]}
              />
            )}
            <div onClick={toggleExpand}>
              <AccordionCaret up={isExpanded} size={16} />
            </div>
          </div>
        )}
        {isEditing && (
          <div className={cx('actions')}>
            <IconButton type="mini" onClick={doSave} Icon={RiCheckLine} />
            <IconButton type="mini" onClick={() => setEditing(false)} Icon={RiCloseLine} />
          </div>
        )}
      </div>
      {isExpanded && (
        <>
          {project.missions.length === 0 && (
            <div className={cx('empty')}>
              <EmptyList />
            </div>
          )}
          {project.missions.map((mission) => (
            <MissionRow key={mission.id} mission={mission} />
          ))}
        </>
      )}
    </div>
  );
};

export default ProjectRow;
